import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * @param {number} cents
 * @param {string} [format]
 * @param {string} [currency]
 * @returns {string}
 */
function formatCartHeaderMoney(cents, format, currency) {
  const moneyFormat = format || '{{amount}}';
  const moneyCurrency = currency || (typeof Shopify !== 'undefined' ? Shopify.currency?.active : 'USD') || 'USD';

  try {
    const formatted = formatMoney(cents, moneyFormat, moneyCurrency);
    return formatted.replace(/\.00$/, '');
  } catch {
    const amount = cents / 100;
    return amount % 1 === 0 ? `$${amount}` : `$${amount.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
  }
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} [cartTotal] - The header cart total price element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('dcoded-cart-drawer-refreshed', this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.syncSessionCountWithVisibleCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('dcoded-cart-drawer-refreshed', this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getCartTotalElement() {
    return this.refs.cartTotal ?? this.querySelector('.header-actions__cart-total');
  }

  /**
   * @param {number} totalCents
   */
  #updateCartTotal(totalCents) {
    const cartTotalEl = this.#getCartTotalElement();
    if (!cartTotalEl) return;

    cartTotalEl.textContent = formatCartHeaderMoney(
      totalCents,
      this.dataset.moneyFormat,
      this.dataset.currency
    );
  }

  /**
   * @returns {Promise<{ item_count: number, total_price: number } | null>}
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
      });

      if (!response.ok) return null;

      const cart = await response.json();
      const itemCount = Number(cart.item_count);
      const totalPrice = Number(cart.total_price);

      if (!Number.isFinite(itemCount) || !Number.isFinite(totalPrice)) return null;

      return { item_count: itemCount, total_price: totalPrice };
    } catch (error) {
      console.error('Cart icon:', error);
      return null;
    }
  }

  /**
   * @param {{ item_count: number, total_price: number }} cart
   * @param {{ animate?: boolean }} [options]
   */
  async #applyCartState(cart, options = {}) {
    const { animate = true } = options;
    const itemCount = cart.item_count;
    const previousCount = this.currentCartCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;
    this.#updateCartTotal(cart.total_price);

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0 || itemCount <= previousCount) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  }

  /**
   * @param {Record<string, unknown>} resource
   * @returns {{ item_count: number, total_price: number } | null}
   */
  #cartFromResource(resource) {
    if (!resource || typeof resource !== 'object') return null;

    const itemCount = Number(resource.item_count);
    const totalPrice = Number(resource.total_price);

    if (!Number.isFinite(itemCount) || !Number.isFinite(totalPrice)) return null;

    return { item_count: itemCount, total_price: totalPrice };
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles cart update events by syncing count and total from the live cart.
   * @param {CartUpdateEvent | Event} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const resource = /** @type {CartUpdateEvent} */ (event).detail?.resource;
    const cartFromEvent = this.#cartFromResource(resource);

    if (cartFromEvent) {
      await this.#applyCartState(cartFromEvent);
      return;
    }

    const cart = await this.#fetchCart();
    if (cart) {
      await this.#applyCartState(cart);
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;

    const cart = await this.#fetchCart();

    if (cart) {
      await this.#applyCartState(cart, { animate: false });
      return;
    }

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (visibleCount !== '' && value !== visibleCount) {
        sessionStorage.setItem(
          'cart-count',
          JSON.stringify({
            value: visibleCount,
            timestamp: Date.now(),
          })
        );
        return;
      }

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0 && visibleCount === '') {
          await this.#applyCartState({ item_count: count, total_price: 0 }, { animate: false });
        }
      }
    } catch {
      // no-op
    }
  };

  syncSessionCountWithVisibleCount = () => {
    if (!this.refs.cartBubbleCount) return;

    const visibleCount = (this.refs.cartBubbleCount.textContent || '').trim();
    if (visibleCount === '') return;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: visibleCount,
        timestamp: Date.now(),
      })
    );
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
